// Server-sent events over fetch (EventSource cannot POST): the browser reads
// the app's summary streams with it, the server the Codex backend's stream.
// Environment-neutral (fetch/ReadableStream/TextDecoder exist in both).

export interface ServerSentEvent {
  /** `event:` field, "message" if absent. */
  event: string;
  /** `data:` lines joined with "\n". */
  data: string;
  id?: string;
}

/**
 * Parses a text/event-stream body following the WHATWG rules: lines end with
 * CRLF, LF or CR; a blank line dispatches; `:` starts a comment (keep-alive);
 * multiple `data:` lines are joined with "\n"; one leading space after the
 * colon is removed. Events without data are skipped.
 */
export async function* readServerSentEvents(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
  onChunk?: () => void,
): AsyncGenerator<ServerSentEvent, void, void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();
  const onAbort = () => {
    reader.cancel().catch(() => {});
  };
  signal?.addEventListener('abort', onAbort, { once: true });
  let completed = false;
  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const { value, done } = await reader.read();
      if (done) break;
      onChunk?.();
      yield* parser.push(decoder.decode(value, { stream: true }));
    }
    completed = true;
    yield* parser.push(decoder.decode());
    yield* parser.end();
  } finally {
    signal?.removeEventListener('abort', onAbort);
    // The consumer stopped early (e.g. after `done`) or an error occurred: close the connection.
    if (!completed) reader.cancel().catch(() => {});
  }
}

/** Incremental SSE parser; exported for tests. */
export function createSseParser() {
  let buffer = '';
  let skipLeadingLf = false;
  let eventType = '';
  let data: string[] = [];
  let hasData = false;
  let id: string | undefined;

  function dispatch(): ServerSentEvent | null {
    const result: ServerSentEvent | null = hasData
      ? { event: eventType || 'message', data: data.join('\n'), ...(id !== undefined ? { id } : {}) }
      : null;
    eventType = '';
    data = [];
    hasData = false;
    return result;
  }

  function processLine(line: string): ServerSentEvent | null {
    if (line === '') return dispatch();
    if (line.startsWith(':')) return null;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        eventType = value;
        break;
      case 'data':
        data.push(value);
        hasData = true;
        break;
      case 'id':
        if (!value.includes('\0')) id = value;
        break;
      default:
        // `retry` and unknown fields are ignored.
        break;
    }
    return null;
  }

  return {
    *push(chunk: string): Generator<ServerSentEvent, void, void> {
      if (!chunk) return;
      if (skipLeadingLf && chunk.startsWith('\n')) chunk = chunk.slice(1);
      skipLeadingLf = false;
      buffer += chunk;
      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch !== '\n' && ch !== '\r') continue;
        const line = buffer.slice(start, i);
        if (ch === '\r') {
          if (i + 1 < buffer.length) {
            if (buffer[i + 1] === '\n') i++;
          } else {
            skipLeadingLf = true;
          }
        }
        start = i + 1;
        const event = processLine(line);
        if (event) yield event;
      }
      buffer = buffer.slice(start);
    },
    /** End of stream: a trailing event without the final blank line is still delivered. */
    *end(): Generator<ServerSentEvent, void, void> {
      if (buffer) {
        const event = processLine(buffer);
        buffer = '';
        if (event) yield event;
      }
      const event = dispatch();
      if (event) yield event;
    },
  };
}
